import { Controller } from "@hotwired/stimulus";
import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp, Search } from "lucide-react";
import { createRoot } from "react-dom/client";

const GridChart = ({ id, legend = true, data }) => {
  const dataArray = Object.entries(data).map(([name, value]) => ({
    name,
    value,
  }));

  const min = dataArray[0];
  const max = dataArray[dataArray.length - 1];
  const minValue = Math.min(...dataArray.map((item) => item.value));
  const maxValue = Math.max(...dataArray.map((item) => item.value));

  // Apply power scale transformation to compress the range
  const powScale = 0.4; // Adjust between 0.3-0.5 for different levels of compression
  const minVisiblePercentage = 0.4; // Minimum bar height as percentage of max (increased to 40%)

  // Transform the values using power scale
  const transformedData = dataArray.map((item) => {
    // Apply power transformation - compresses high values more than low values
    const powValue = Math.pow(item.value, powScale);
    return {
      ...item,
      powValue,
    };
  });

  // Get min and max of transformed values
  const minPowValue = Math.min(...transformedData.map((item) => item.powValue));
  const maxPowValue = Math.max(...transformedData.map((item) => item.powValue));

  // Normalize and scale the transformed values
  const visualData = transformedData.map((item) => {
    // Normalize to 0-1 range
    const normalizedValue =
      (item.powValue - minPowValue) / (maxPowValue - minPowValue);
    // Scale to minVisible-1 range
    const scaledValue =
      minVisiblePercentage + normalizedValue * (1 - minVisiblePercentage);
    // Scale back to actual value range for visualization
    const visualValue = scaledValue * maxValue;

    return {
      ...item,
      visualValue,
    };
  });

  return (
    <div className="h-full relative flex flex-col justify-between">
      <div className="inherit-height flex-1 flex items-end pt-6 px-6">
        <ResponsiveContainer width="100%" height={legend ? 180 : 240}>
          <BarChart
            data={visualData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <YAxis
              domain={[
                // Minimum domain with padding (adjusted for higher minimum)
                minVisiblePercentage * maxValue * 0.85,
                // Maximum domain with padding
                maxValue * 1.05,
              ]}
              hide
            />
            <Tooltip
              cursor={false}
              content={({ payload }) =>
                payload?.length ? (
                  <div className="bg-white p-2 rounded shadow">
                    <p className="text-sm font-medium">
                      {payload[0].payload.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {payload[0].payload.value}
                    </p>
                  </div>
                ) : null
              }
            />
            <defs>
              <linearGradient id={`gradient-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7277dc" stopOpacity={1} />
                <stop offset="100%" stopColor="#abaeed" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <Bar
              dataKey="visualValue"
              fill={`url(#gradient-${id})`}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {legend && (
        <div className="inline-flex flex-col border-t bg-white shadow">
          <div className="text-xs py-2 px-4 border-b w-full min-w-0 text-gray-600">
            {legend}
          </div>
          <div className="grid grid-cols-2 divide-x">
            <div className="flex flex-col px-4 py-2 relative">
              <span className="text-xs text-gray-600 truncate w-full min-w-0">
                {min.name}
              </span>
              <span className="text-lg font-bold text-default-600">
                {min.value}
              </span>
              <div className="absolute right-0 top-1/2 bg-white w-6 h-6 ring-1 ring-gray-200 rounded-full flex items-center justify-center -translate-y-1/2  translate-x-1/2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div className="flex flex-col px-6 py-2">
              <span className="text-xs text-gray-600 truncate w-full min-w-0">
                {max.name}
              </span>
              <span className="text-lg font-bold text-default-600">
                {max.value}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default class extends Controller {
  connect() {
    const legend = this.element.dataset.legend;
    const id = this.element.dataset.id;
    const data = JSON.parse(this.element.dataset.data || "{}");
    this.root = createRoot(this.element);
    this.root.render(<GridChart id={id} legend={legend} data={data} />);
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
    }
  }
}
